/**
 * Sync photos serveur ↔ local — Phase 4, incrément 4.9.
 *
 * Premier sync sub-entité **binaire**. Étend ADR 0009 (server-wins)
 * aux photos, avec une particularité majeure :
 *
 *   - **Migration en 3 étapes** : pour chaque photo locale UUID-less
 *     avec blob IDB, on (1) demande une presigned URL, (2) upload
 *     le blob vers R2, (3) confirm côté API. Échec à n'importe quelle
 *     étape → la photo reste locale, retentée au prochain sync.
 *
 *   - **Skip photos sans blob réel** (`img == null` ou autre valeur
 *     non-sentinel) : fallback emoji UI, pas de média à uploader.
 *
 *   - **Le merge ingère les URLs R2 signées** dans le cache mémoire
 *     `photoUrls.ts` — le View s'hydrate au prochain render sans
 *     toucher IDB.
 *
 * Mode local-only préservé (CLAUDE.md §2.4) : si R2 down ou échec
 * réseau, warn console + state local intact.
 */
import * as photosApi from '../api/photos.js';
import { ApiError } from '../api/client.js';
import {
  applyPhotosSyncResult,
  getHomesSnapshot,
  isServerHomeId,
  isUploadablePhoto,
  type ServerPhotoPayload,
} from './homes.js';
import { getBlob } from '../storage/blobs.js';
import type { ClassicHomeData } from '@nestor/shared-types';

export interface PhotosSyncStatus {
  state: 'idle' | 'syncing' | 'ok' | 'error';
  lastAt: number | null;
  errorCode: string | null;
}

let status: PhotosSyncStatus = { state: 'idle', lastAt: null, errorCode: null };
let inFlight: Promise<void> | null = null;

export function getPhotosSyncStatus(): PhotosSyncStatus {
  return status;
}

/**
 * Synchronise les photos pour tous les foyers UUID classiques.
 * Idempotent (dédup via `inFlight`).
 *
 * Stratégie par home :
 *   1. Pour chaque photo locale non-UUID + uploadable (blob IDB) :
 *      - GET upload-url + uploadFields presigned.
 *      - POST multipart vers R2.
 *      - POST /homes/:homeId/photos pour confirm + insert row.
 *      - Map locale id → server uuid.
 *   2. GET /homes/:homeId/photos pour la liste autoritaire.
 *   3. `applyPhotosSyncResult(homeId, …)` atomique.
 *
 * Erreurs ponctuelles : warn + continue. Les blobs locaux non
 * migrés restent disponibles via cache mémoire `photoUrls`.
 */
export async function syncPhotosForAllHomes(): Promise<void> {
  if (inFlight) return inFlight;
  inFlight = doSync().finally(() => {
    inFlight = null;
  });
  return inFlight;
}

async function doSync(): Promise<void> {
  status = { state: 'syncing', lastAt: status.lastAt, errorCode: null };
  const { homes } = getHomesSnapshot();

  let anyError = false;
  let lastErrorCode: string | null = null;

  for (const home of homes) {
    if (!isServerHomeId(home.id)) continue;
    if (home.mode !== 'classic') continue;

    const data = home.data as ClassicHomeData;

    // 1. Migration : photos locales pré-auth avec blob IDB.
    const idMap = new Map<string, string>();
    const migratable = data.photos.filter((p) => !isServerHomeId(p.id) && isUploadablePhoto(p));
    for (const photo of migratable) {
      try {
        const blob = await getBlob(`photo:${photo.id}`);
        if (!blob) {
          // Sentinel `[idb]` mais blob introuvable — on skip, pas
          // grand-chose à faire. Le cache mémoire peut tout de même
          // afficher si `photoUrls` a encore une URL pour cet id.
          console.warn('[photos-sync] blob IDB manquant', { photoId: photo.id });
          continue;
        }
        // Phase 3.3.6 : `uploadPhotoBlob` retourne `SendResult`
        // offline-first. Sur `kind: 'sent'`, on remap idMap ; sur
        // `kind: 'queued'`, on garde le local id pour le replay.
        // Phase 3.3.7 : passe le local id pour que le broker
        // remap émette `nestor:mutation-remap` au replay success
        // (cohérent avec events/tasks/etc.).
        const result = await photosApi.uploadPhotoBlob(
          home.id,
          blob,
          {
            ...(photo.caption ? { caption: photo.caption } : {}),
            ...(photo.album ? { albumName: photo.album } : {}),
            ...(photo.takenAt ? { takenAt: new Date(photo.takenAt).toISOString() } : {}),
          },
          photo.id,
        );
        if (result.kind === 'sent') {
          idMap.set(photo.id, result.data.photo.id);
        }
      } catch (err) {
        const code = errorCode(err);
        anyError = true;
        lastErrorCode = code;
        console.warn('[photos-sync] migration upload échouée', {
          homeId: home.id,
          photoId: photo.id,
          error: code,
        });
      }
    }

    // 2. Fetch + 3. apply.
    try {
      const serverPhotos = await photosApi.listPhotos(home.id);
      const payload: ServerPhotoPayload[] = serverPhotos.map((p) => ({
        id: p.id,
        url: p.url,
        caption: p.caption,
        albumName: p.albumName,
        takenAt: p.takenAt,
      }));
      applyPhotosSyncResult(home.id, { idMap, serverPhotos: payload });
    } catch (err) {
      if (idMap.size > 0) {
        applyPhotosSyncResult(home.id, { idMap });
      }
      const code = errorCode(err);
      anyError = true;
      lastErrorCode = code;
      console.warn('[photos-sync] fetch /photos échoué', { homeId: home.id, error: code });
    }
  }

  status = anyError
    ? { state: 'error', lastAt: Date.now(), errorCode: lastErrorCode }
    : { state: 'ok', lastAt: Date.now(), errorCode: null };
}

function errorCode(err: unknown): string {
  if (err instanceof ApiError) {
    return err.code ?? `http_${err.status.toString()}`;
  }
  if (err instanceof Error) return err.name;
  return 'unknown';
}

/** @internal pour tests — reset le state module-level. */
export function _resetPhotosSyncForTests(): void {
  status = { state: 'idle', lastAt: null, errorCode: null };
  inFlight = null;
}
